import styles from './GridView.module.scss';
import type { TileAspectRatio } from './interfaces';

export const tileClassNames: Record<number, string | undefined> = {
  1: styles.tile__x1,
  2: styles.tile__x2,
  3: styles.tile__x3,
};

/**
 * Columns a tile may span, clamped to what `tileClassNames` actually provides.
 *
 * `Category.tileSize` comes straight from the API as `Nullable<number>`. An
 * out-of-range value used to reach `tileClassNames` unclamped and return
 * `undefined`, leaving the tile with no `grid-column` and no `aspect-ratio` at
 * all — it collapsed in the grid. Everything that derives from the span, the
 * grid class and the image request alike, has to agree on the same number.
 */
export const resolveTileSpans = (tileSize?: number | null): number => {
  if (typeof tileSize !== 'number' || !Number.isFinite(tileSize)) {
    return 1;
  }

  return Math.min(Math.max(Math.round(tileSize), 1), 3);
};

/**
 * Candidates for a tile. The narrowest covers a single-span tile on a phone,
 * the widest a triple-span tile on desktop at 2x density.
 */
export const TILE_WIDTHS = [320, 480, 800, 1280, 1920];

/**
 * Columns the default bento grid splits a row into, per breakpoint — see the
 * `bento-grid` mixin. Blocks that override the grid pass their own `sizes`
 * instead.
 */
const MOBILE_COLUMNS = 3;
const DESKTOP_COLUMNS = 4;

/**
 * Width the desktop layout caps a full row at. This is MCosmetic's
 * `--layout-max-width`; the other tenants cap at 1200px, so their tiles are
 * advertised about 17% wider than they are. Overstating a box only ever picks a
 * larger candidate, never a blurry one, which is why one value serves all.
 */
const DESKTOP_ROW_WIDTH = 1400;

export const TILE_DESKTOP_MIN_WIDTH = 1025;

const DESKTOP_TILE_RATIO_SCALE = 1.3;

/**
 * `sizes` for a tile laid out by the default bento grid: `spans` columns out of
 * three on mobile and out of four on desktop. The query matches
 * `more-than-mobile`, which is where the column count changes.
 */
export const bentoTileSizes = (spans: number) =>
  `(min-width: ${TILE_DESKTOP_MIN_WIDTH}px) ${Math.round(
    (DESKTOP_ROW_WIDTH / DESKTOP_COLUMNS) * spans,
  )}px, ${Math.round((100 / MOBILE_COLUMNS) * spans * 100) / 100}vw`;

export const bentoTileRatios = (spans: number): TileAspectRatio => ({
  mobile: spans,
  desktop: spans * DESKTOP_TILE_RATIO_SCALE,
});
